import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase, Site, Worker, Induction } from '../lib/supabase';
import { getCurrentAESTDate } from '../utils/dateUtils';
import { 
  Building2, 
  Users, 
  ClipboardCheck, 
  AlertTriangle,
  TrendingUp,
  Calendar,
  Stethoscope,
  Truck,
  CreditCard
} from 'lucide-react';

interface DashboardStats {
  totalSites: number;
  activeSites: number;
  totalWorkers: number;
  totalInductions: number;
  expiredInductions: number;
  pendingInductions: number;
  healthMonitoringOverdue: number;
  fitTestingOverdue: number;
  totalVehicles: number;
  regoOverdue: number;
  regoDueSoon: number;
  nhvrOverdue: number;
  nhvrDueSoon: number;
  overdueVehicleNames: { name: string; nhvrDate?: string }[];
  dueSoonVehicleNames: { name: string; nhvrDate?: string }[];
  licenceExpired: { name: string; expiry: string }[];
  licenceExpiringSoon: { name: string; expiry: string; days: number }[];
}

export const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalSites: 0,
    activeSites: 0,
    totalWorkers: 0,
    totalInductions: 0,
    expiredInductions: 0,
    pendingInductions: 0,
    healthMonitoringOverdue: 0,
    fitTestingOverdue: 0,
    totalVehicles: 0,
    regoOverdue: 0,
    regoDueSoon: 0,
    nhvrOverdue: 0,
    nhvrDueSoon: 0,
    overdueVehicleNames: [],
    dueSoonVehicleNames: [],
    licenceExpired: [],
    licenceExpiringSoon: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const { data: workersData, error: workersError } = await supabase
        .from('workers')
        .select('id, name, role, status, requires_health_monitoring, fit_testing_not_applicable, drivers_licence_expiry')
        .eq('status', 'active')
        .order('name');

      if (workersError) throw workersError;

      const { data: healthData, error: healthError } = await supabase
        .from('health_records')
        .select('*')
        .order('created_at', { ascending: false });

      if (healthError && healthError.code !== 'PGRST116') {
        console.error('Error fetching health records:', healthError);
      }

      const [sitesData, inductionsData] = await Promise.all([
        supabase.from('sites').select('*').eq('status', 'active'),
        supabase.from('inductions').select('*')
      ]);

      const [vehiclesData, inspectionsData] = await Promise.all([
        supabase.from('vehicles').select('id, registration, type, make, model, year, rego_expiry_date, rego_na, nhvr_required'),
        supabase.from('vehicle_inspections').select('vehicle_id, inspection_type, expiry_date').order('expiry_date', { ascending: false })
      ]);

      const totalSites = sitesData.data?.length || 0;
      const activeSites = totalSites;
      const totalWorkers = workersData?.length || 0;
      const totalInductions = inductionsData.data?.length || 0;
      const pendingInductions = inductionsData.data?.filter(i => i.status === 'pending').length || 0;
      const completedInductions = inductionsData.data?.filter(i => i.status === 'completed').length || 0;

      const today = getCurrentAESTDate();

      const licenceExpired: { name: string; expiry: string }[] = [];
      const licenceExpiringSoon: { name: string; expiry: string; days: number }[] = [];
      (workersData || []).forEach(w => {
        if (!w.drivers_licence_expiry) return;
        const days = Math.round((new Date(w.drivers_licence_expiry).getTime() - today.getTime()) / 86_400_000);
        if (days < 0) licenceExpired.push({ name: w.name, expiry: w.drivers_licence_expiry });
        else if (days <= 30) licenceExpiringSoon.push({ name: w.name, expiry: w.drivers_licence_expiry, days });
      });
      licenceExpiringSoon.sort((a, b) => a.days - b.days);

      const workersRequiringHealthMonitoring = (workersData || []).filter(w => w.requires_health_monitoring);

      const healthMonitoringOverdueCount = workersRequiringHealthMonitoring.filter(worker => {
        const latestRecord = (healthData || []).find(r => r.worker_id === worker.id);
        return !latestRecord?.health_monitoring_date ||
               (latestRecord.health_monitoring_due_date && new Date(latestRecord.health_monitoring_due_date) < today);
      }).length;

      const fitTestingOverdueCount = workersRequiringHealthMonitoring.filter(worker => {
        if (worker.fit_testing_not_applicable) return false;
        const latestRecord = (healthData || []).find(r => r.worker_id === worker.id);
        return !latestRecord?.fit_testing_date ||
               (latestRecord.fit_testing_due_date && new Date(latestRecord.fit_testing_due_date) < today);
      }).length;

      // Vehicle compliance — skip rego_na vehicles for rego counts
      const totalVehicles = vehiclesData.data?.length || 0;
      let regoOverdue = 0;
      let regoDueSoon = 0;
      let nhvrOverdue = 0;
      let nhvrDueSoon = 0;
      const overdueMap = new Map<string, string | undefined>();
      const dueSoonMap = new Map<string, string | undefined>();

      (vehiclesData.data || []).forEach(vehicle => {
        const descParts = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean);
        const desc = descParts.length > 0 ? descParts.join(' ') : (vehicle.type || 'Vehicle');
        const reg = vehicle.registration ? `${desc} (${vehicle.registration})` : desc;

        if (!vehicle.rego_na && vehicle.rego_expiry_date) {
          const days = Math.ceil((new Date(vehicle.rego_expiry_date).getTime() - today.getTime()) / 86_400_000);
          if (days < 0) { regoOverdue++; overdueMap.set(reg, undefined); }
          else if (days <= 30) { regoDueSoon++; dueSoonMap.set(reg, undefined); }
        }

        if (vehicle.nhvr_required === true) {
          const latest = (inspectionsData.data || []).find(
            i => i.vehicle_id === vehicle.id && i.inspection_type === 'nhvr'
          );
          if (!latest) {
            nhvrOverdue++;
            overdueMap.set(reg, undefined);
          } else {
            const days = Math.ceil((new Date(latest.expiry_date).getTime() - today.getTime()) / 86_400_000);
            if (days < 0) { nhvrOverdue++; overdueMap.set(reg, latest.expiry_date); }
            else if (days <= 30) { nhvrDueSoon++; dueSoonMap.set(reg, latest.expiry_date); }
          }
        }
      });

      setStats({
        totalSites,
        activeSites,
        totalWorkers,
        totalInductions: completedInductions,
        expiredInductions: 0,
        pendingInductions,
        healthMonitoringOverdue: healthMonitoringOverdueCount,
        fitTestingOverdue: fitTestingOverdueCount,
        totalVehicles,
        regoOverdue,
        regoDueSoon,
        nhvrOverdue,
        nhvrDueSoon,
        overdueVehicleNames: Array.from(overdueMap.entries()).map(([name, nhvrDate]) => ({ name, nhvrDate })),
        dueSoonVehicleNames: Array.from(dueSoonMap.entries()).filter(([n]) => !overdueMap.has(n)).map(([name, nhvrDate]) => ({ name, nhvrDate })),
        licenceExpired,
        licenceExpiringSoon,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
  }> = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const vehicleOverdueCount = stats.regoOverdue + stats.nhvrOverdue;
  const vehicleDueSoonCount = stats.regoDueSoon + stats.nhvrDueSoon;
  const allClear =
    stats.pendingInductions === 0 &&
    stats.healthMonitoringOverdue === 0 &&
    stats.fitTestingOverdue === 0 &&
    vehicleOverdueCount === 0 &&
    vehicleDueSoonCount === 0 &&
    stats.licenceExpired.length === 0 &&
    stats.licenceExpiringSoon.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <Calendar className="h-4 w-4" />
          <span>Last updated: {new Date().toLocaleDateString()}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard title="Active Sites" value={stats.activeSites} icon={Building2} color="bg-blue-500" />
        <StatCard title="Total Workers" value={stats.totalWorkers} icon={Users} color="bg-green-500" />
        <StatCard title="Completed Inductions" value={stats.totalInductions} icon={ClipboardCheck} color="bg-orange-500" />
        <StatCard title="Pending Inductions" value={stats.pendingInductions} icon={AlertTriangle} color="bg-yellow-500" />
        <StatCard title="All Sites" value={stats.activeSites} icon={Building2} color="bg-purple-500" />
        <StatCard title="Total Vehicles" value={stats.totalVehicles} icon={Truck} color="bg-blue-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Safety Alerts</h3>
          <div className="space-y-3">

            <style>{`
              @keyframes alert-pulse-red {
                0%, 100% { box-shadow: 0 0 4px 1px rgba(239,68,68,0.2), 0 0 0 1px rgba(185,28,28,0.3); background-color: rgb(254 226 226); border-color: rgb(252,165,165); }
                50% { box-shadow: 0 0 16px 6px rgba(220,38,38,0.55), 0 0 0 3px rgba(185,28,28,0.85); background-color: rgb(254 202 202); border-color: rgb(185,28,28); }
              }
              @keyframes alert-pulse-amber {
                0%, 100% { box-shadow: 0 0 4px 1px rgba(245,158,11,0.2), 0 0 0 1px rgba(180,83,9,0.3); background-color: rgb(254 243 199); border-color: rgb(253,230,138); }
                50% { box-shadow: 0 0 16px 6px rgba(217,119,6,0.55), 0 0 0 3px rgba(180,83,9,0.85); background-color: rgb(253 230 138); border-color: rgb(180,83,9); }
              }
            `}</style>

            {/* Health testing */}
            {(stats.healthMonitoringOverdue > 0 || stats.fitTestingOverdue > 0) && (
              <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg"
                style={{ animation: 'alert-pulse-red 2s ease-in-out infinite' }}>
                <Stethoscope className="h-5 w-5 text-red-500 mr-3 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800">Health Testing Overdue</p>
                  <p className="text-xs text-red-600">
                    {stats.healthMonitoringOverdue} employee{stats.healthMonitoringOverdue !== 1 ? 's' : ''} overdue for health monitoring
                    {stats.fitTestingOverdue > 0 && ` • ${stats.fitTestingOverdue} overdue for fit testing`}
                  </p>
                </div>
              </div>
            )}

            {/* Pending inductions */}
            {stats.pendingInductions > 0 && (
              <div className="flex items-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-yellow-500 mr-3 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">{stats.pendingInductions} Pending Induction{stats.pendingInductions !== 1 ? 's' : ''}</p>
                  <p className="text-xs text-yellow-600">Workers awaiting site induction completion</p>
                </div>
              </div>
            )}

            {/* Vehicle compliance — overdue (red) */}
            {vehicleOverdueCount > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg"
                style={{ animation: 'alert-pulse-red 2s ease-in-out infinite' }}>
                <div className="flex items-start">
                  <Truck className="h-5 w-5 text-red-500 mr-3 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-red-800">Vehicle Compliance — Overdue</p>
                      <span className="ml-2 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 bg-red-500 text-white text-xs font-bold rounded-full shrink-0">
                        {vehicleOverdueCount}
                      </span>
                    </div>
                    <p className="text-xs text-red-600 mt-0.5">
                      {stats.regoOverdue > 0 && `${stats.regoOverdue} rego expired`}
                      {stats.regoOverdue > 0 && stats.nhvrOverdue > 0 && ' • '}
                      {stats.nhvrOverdue > 0 && `${stats.nhvrOverdue} NHVR overdue`}
                    </p>
                    {stats.overdueVehicleNames.length > 0 && (
                      <ul className="mt-1.5 space-y-0.5">
                        {stats.overdueVehicleNames.map((v, i) => (
                          <li key={i} className="text-xs text-red-700 font-medium flex items-center gap-1">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                            {v.name}
                            {v.nhvrDate && (
                              <span className="text-red-500 font-normal"> — NHVR {new Date(v.nhvrDate).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Vehicle compliance — due soon (amber) */}
            {vehicleDueSoonCount > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg"
                style={{ animation: 'alert-pulse-amber 2s ease-in-out infinite' }}>
                <div className="flex items-start">
                  <Truck className="h-5 w-5 text-amber-500 mr-3 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-amber-800">Vehicle Compliance — Due Soon</p>
                      <span className="ml-2 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 bg-amber-500 text-white text-xs font-bold rounded-full shrink-0">
                        {vehicleDueSoonCount}
                      </span>
                    </div>
                    <p className="text-xs text-amber-600 mt-0.5">
                      {stats.regoDueSoon > 0 && `${stats.regoDueSoon} rego expiring within 30 days`}
                      {stats.regoDueSoon > 0 && stats.nhvrDueSoon > 0 && ' • '}
                      {stats.nhvrDueSoon > 0 && `${stats.nhvrDueSoon} NHVR due within 30 days`}
                    </p>
                    {stats.dueSoonVehicleNames.length > 0 && (
                      <ul className="mt-1.5 space-y-0.5">
                        {stats.dueSoonVehicleNames.map((v, i) => (
                          <li key={i} className="text-xs text-amber-700 font-medium flex items-center gap-1">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                            {v.name}
                            {v.nhvrDate && (
                              <span className="text-amber-600 font-normal"> — NHVR {new Date(v.nhvrDate).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Driver's licences — expired (red) */}
            {stats.licenceExpired.length > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg"
                style={{ animation: 'alert-pulse-red 2s ease-in-out infinite' }}>
                <div className="flex items-start">
                  <CreditCard className="h-5 w-5 text-red-500 mr-3 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-red-800">Driver's Licence — Expired</p>
                      <span className="ml-2 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 bg-red-500 text-white text-xs font-bold rounded-full shrink-0">
                        {stats.licenceExpired.length}
                      </span>
                    </div>
                    <p className="text-xs text-red-600 mt-0.5">
                      {stats.licenceExpired.length} worker{stats.licenceExpired.length !== 1 ? 's' : ''} holding an expired driver's licence
                    </p>
                    <ul className="mt-1.5 space-y-0.5">
                      {stats.licenceExpired.map((w, i) => (
                        <li key={i} className="text-xs text-red-700 font-medium flex items-center gap-1">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                          {w.name} — expired {new Date(w.expiry).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Driver's licences — expiring soon (amber) */}
            {stats.licenceExpiringSoon.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg"
                style={{ animation: 'alert-pulse-amber 2s ease-in-out infinite' }}>
                <div className="flex items-start">
                  <CreditCard className="h-5 w-5 text-amber-500 mr-3 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-amber-800">Driver's Licence — Expiring Soon</p>
                      <span className="ml-2 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 bg-amber-500 text-white text-xs font-bold rounded-full shrink-0">
                        {stats.licenceExpiringSoon.length}
                      </span>
                    </div>
                    <p className="text-xs text-amber-600 mt-0.5">
                      {stats.licenceExpiringSoon.length} worker{stats.licenceExpiringSoon.length !== 1 ? 's' : ''} with licence expiring within 30 days
                    </p>
                    <ul className="mt-1.5 space-y-0.5">
                      {stats.licenceExpiringSoon.map((w, i) => (
                        <li key={i} className="text-xs text-amber-700 font-medium flex items-center gap-1">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                          {w.name} — expires {new Date(w.expiry).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })} ({w.days}d)
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* All clear */}
            {allClear && (
              <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
                <ClipboardCheck className="h-5 w-5 text-green-500 mr-3 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-800">All requirements up to date</p>
                  <p className="text-xs text-green-600">Safety, health compliance, and vehicle registrations are current</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Link to="/sites" className="block w-full text-left p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
              <div className="flex items-center">
                <Building2 className="h-5 w-5 text-orange-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-orange-800">Add New Site</p>
                  <p className="text-xs text-orange-600">Create a new demolition site</p>
                </div>
              </div>
            </Link>
            <Link to="/workers" className="block w-full text-left p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-blue-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Register Worker</p>
                  <p className="text-xs text-blue-600">Add new worker to database</p>
                </div>
              </div>
            </Link>
            <Link to="/inductions" className="block w-full text-left p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
              <div className="flex items-center">
                <ClipboardCheck className="h-5 w-5 text-green-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-green-800">Complete Induction</p>
                  <p className="text-xs text-green-600">Process worker site induction</p>
                </div>
              </div>
            </Link>
            <Link to="/health-monitoring" className="block w-full text-left p-3 bg-pink-50 rounded-lg hover:bg-pink-100 transition-colors">
              <div className="flex items-center">
                <Stethoscope className="h-5 w-5 text-pink-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-pink-800">Health Monitoring</p>
                  <p className="text-xs text-pink-600">Manage health testing compliance</p>
                </div>
              </div>
            </Link>
            <Link to="/vehicles" className="block w-full text-left p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
              <div className="flex items-center">
                <Truck className="h-5 w-5 text-purple-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-purple-800">Vehicle Management</p>
                  <p className="text-xs text-purple-600">Track registrations and inspections</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
